import React, { useEffect, useMemo, useRef } from 'react';
import navItems from './navigationItems';
import { useDispatch, useSelector } from 'react-redux';
import {
  ClearedLink,
  ClearedExternalLink,
} from '../../common/controls/Controls';
import Avatar from '../Navigation/AppBarCMS/Avatar';
import FeedBack from '../Navigation/AppBarCMS/FeedBack';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import IconButton from '../../common/controls/IconButton/IconButton';
import Tooltip from '../../common/Tooltip/Tooltip';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import {
  toggleCMSNavigationGroup,
  toggleCMSSidebar,
} from '../../../redux/slices/localStorageSlice';
import useCheckIfUserCanSeePagesInMenu from '../../../hooks/useCheckIfUserCanSeePagesInMenu';
import { menuApi } from '../../../redux/services/menu';
import CollapsibleMenu from './CollapsibleMenu';
import useUserHasRole from '../../../hooks/useUserHasRoles.jsx';
import {
  ROLE_EXTERNAL_PARTNER,
  ROLE_EXTERNAL_USER,
  ROLE_ING_PROJECTS_ADMIN,
  ROLE_ING_PROJECTS_GUARANTOR,
  ROLE_ING_PROJECT_STUDENT,
  ROLE_STUDY_PROGRAM_GUARANTEE,
  ROLE_ING_PROJECTS_ASSIGNMENT_COORDINATOR,
} from '../../../utils/constants/roles.js';

const MOBILE_MAX_WIDTH = 768;
const isMobileWidth = () =>
  typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_WIDTH;

const getRoleForPermission = (permission) => {
  switch (permission) {
    case 'ing-projects-admin':
      return ROLE_ING_PROJECTS_ADMIN;
    case 'ing-projects-project-guarantee':
      return ROLE_ING_PROJECTS_GUARANTOR;
    case 'ing-projects-study-program-guarantee':
      return ROLE_STUDY_PROGRAM_GUARANTEE;
    case 'ing-projects-assignments':
      return ROLE_ING_PROJECTS_ASSIGNMENT_COORDINATOR;
    case 'ing-projects-student':
      return ROLE_ING_PROJECT_STUDENT;
    default:
      return null;
  }
};

const hasRoleAccess = (user, roles) =>
  Array.isArray(roles) &&
  roles.length > 0 &&
  roles.some((role) => (user?.roles || []).some((userRole) => userRole?.name === role));

const SidebarCMS = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector((s) => s.auth.user);
  const openedMenuItems = useSelector((s) => s.localStorage.openedMenuItems);
  const isSidebarHidden = useSelector((s) => s.localStorage.isSidebarHidden);

  const isSpecialDomain =
    typeof window !== 'undefined' && window.location.hostname === 'localhost';

  // Keep latest sidebar state in a ref for stable access inside event listeners
  const isSidebarHiddenRef = useRef(isSidebarHidden);
  useEffect(() => {
    isSidebarHiddenRef.current = isSidebarHidden;
  }, [isSidebarHidden]);

  // Track if we auto-collapsed due to mobile so we can auto-expand later when leaving mobile
  const autoCollapsedRef = useRef(false);
  // Track previous isMobile value to detect entering/leaving mobile
  const isMobileRef = useRef(
    typeof window !== 'undefined'
      ? window.innerWidth <= MOBILE_MAX_WIDTH
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Initial check on mount: collapse by default if on mobile and currently expanded
    const initialIsMobile = window.innerWidth <= MOBILE_MAX_WIDTH;
    if (initialIsMobile && !isSidebarHiddenRef.current) {
      dispatch(toggleCMSSidebar(true));
      autoCollapsedRef.current = true;
    }
    isMobileRef.current = initialIsMobile;

    const handleResize = () => {
      const nowMobile = window.innerWidth <= MOBILE_MAX_WIDTH;

      // Entering mobile from non-mobile -> auto-collapse once
      if (nowMobile && !isMobileRef.current) {
        if (!isSidebarHiddenRef.current) {
          dispatch(toggleCMSSidebar(true));
          autoCollapsedRef.current = true;
        }
      }

      // Leaving mobile -> auto-expand only if we auto-collapsed before
      if (!nowMobile && isMobileRef.current) {
        if (isSidebarHiddenRef.current && autoCollapsedRef.current) {
          dispatch(toggleCMSSidebar(false));
          autoCollapsedRef.current = false;
        }
      }

      // Update previous state
      isMobileRef.current = nowMobile;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  // If user manually toggles the sidebar open while on mobile, clear auto flag
  useEffect(() => {
    if (!isSidebarHidden && autoCollapsedRef.current) {
      autoCollapsedRef.current = false;
    }
  }, [isSidebarHidden]);

  const isAllowedToSeePages = useCheckIfUserCanSeePagesInMenu();

  // Compute which groups have at least one visible item for the current user
  const groupsWithoutPermissions = useMemo(() => {
    if (user) {
      const mapping = {}; // {GroupTitle: boolean}
      navItems.forEach((navItemGroup) => {
        mapping[navItemGroup.title] = true;
        navItemGroup.items &&
          navItemGroup.items.forEach((subItem) => {
            if (!subItem.permission) {
              if (subItem.roles && hasRoleAccess(user, subItem.roles)) {
                mapping[navItemGroup.title] = false;
              }
              return;
            }

            const permissions = Array.isArray(subItem.permission)
              ? subItem.permission
              : [subItem.permission];

            const hasAccess = permissions.some((permission) => {
              if (permission && user?.permissions?.[permission]) {
                return true;
              }

              const mappedRoleName = getRoleForPermission(permission);
              return mappedRoleName
                ? (user.roles || []).some((role) => role?.name === mappedRoleName)
                : false;
            });

            const roleAccess = hasRoleAccess(user, subItem.roles);

            if (hasAccess || roleAccess) {
              mapping[navItemGroup.title] = false;
            }
          });
      });
      return mapping;
    }
    return [];
  }, [user]);

  // Auto-open the menu group that matches the current CMS path on direct navigation
  const lastAutoOpenPathRef = useRef('');
  useEffect(() => {
    if (!user) return;
    const path = location.pathname || '';
    if (lastAutoOpenPathRef.current === path) return; // already handled this path

    // Find first group whose any subitem URL is a prefix of current path
    let targetIndex = -1;
    navItems.forEach((groupItem, idx) => {
      if (targetIndex !== -1) return;
      if (Array.isArray(groupItem.items) && groupItem.items.length > 0) {
        const hasMatch = groupItem.items.some(
          (sub) =>
            typeof sub.url === 'string' &&
            !sub.url.includes('://') &&
            path.startsWith(sub.url),
        );
        if (hasMatch) {
          targetIndex = idx;
        }
      }
    });

    if (targetIndex !== -1 && !openedMenuItems.includes(targetIndex)) {
      dispatch(toggleCMSNavigationGroup({ index: targetIndex, value: true }));
    }
    lastAutoOpenPathRef.current = path;
  }, [location.pathname, user, dispatch, openedMenuItems]);

  // If user is External partner or External user, expand all collapsible menu groups they can see
  const hasExternalRole = useUserHasRole([
    ROLE_EXTERNAL_PARTNER,
    ROLE_EXTERNAL_USER,
  ]);
  const allExpandedOnceRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    if (!hasExternalRole) return;

    navItems.forEach((groupItem, idx) => {
      const hasItems =
        Array.isArray(groupItem.items) && groupItem.items.length > 0;
      const canSeeGroup =
        !groupsWithoutPermissions ||
        groupsWithoutPermissions[groupItem.title] === false;
      if (hasItems && canSeeGroup && !openedMenuItems.includes(idx)) {
        dispatch(toggleCMSNavigationGroup({ index: idx, value: true }));
      }
    });
    allExpandedOnceRef.current = true;
  }, [
    hasExternalRole,
    user,
    groupsWithoutPermissions,
    openedMenuItems,
    dispatch,
  ]);

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const savedScroll = localStorage.getItem('scrollPosition');
    if (savedScroll && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = parseInt(savedScroll, 10);
    }
  }, []);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      localStorage.setItem(
        'scrollPosition',
        scrollContainerRef.current.scrollTop,
      );
    }
  };

  useEffect(() => {
    if (user && user.permissions) {
      dispatch(menuApi.util.resetApiState());
    }
  }, [user, dispatch]);

  if (!user) {
    return null;
  }
  return (
    <>
      <div
        className="sidebar-logo-container"
        style={{ backgroundColor: isSpecialDomain ? 'Tomato' : '#ffb81c' }}
      >
        <ClearedLink to={'/'}>
          <img src="/images/fri_logo_male.png" alt="Logo FRI" />
          <div className="styled-title">{t('Portál FRI')}</div>
        </ClearedLink>

        <div className="toggle-icon">
          <IconButton
            onClick={(e) => {
              dispatch(toggleCMSSidebar(!isSidebarHidden));
              e.currentTarget.blur();
            }}
            tooltip={isSidebarHidden ? t('Rozbaliť') : t('Zbaliť')}
            icon={
              isSidebarHidden ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
            }
            style={{ color: 'white' }}
          />
        </div>
      </div>
      <div className="items-wrapper">
        <div
          className="navigation-items-wrapper"
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          {navItems.map((groupItem, index) => {
            if (
              !user ||
              !user.permissions ||
              (groupItem.permission && !user.permissions[groupItem.permission])
            ) {
              return <React.Fragment key={groupItem.title} />;
            }

            if (groupItem.url) {
              return (
                <div key={groupItem.title}>
                  <div
                    onClick={() => {
                      dispatch(
                        toggleCMSNavigationGroup({
                          index,
                          value: false,
                          expandSidebar: true,
                        }),
                      );
                    }}
                    className={`menu-main-item ${
                      location.pathname === groupItem.url && 'active-item'
                    }`}
                  >
                    {groupItem.url.includes('.fri.uniza.sk') ? (
                      <ClearedExternalLink
                        href={groupItem.url}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isMobileWidth()) {
                            dispatch(toggleCMSSidebar(true));
                          }
                        }}
                      >
                        <Tooltip
                          tooltip={
                            isSidebarHidden ? t(groupItem.title) : undefined
                          }
                          right
                        >
                          <span
                            style={{
                              marginRight: isSidebarHidden ? '0' : '10px',
                            }}
                          >
                            {groupItem.icon}
                          </span>
                        </Tooltip>

                        <span className="external-first-level">
                          {t(groupItem.title)}
                        </span>
                      </ClearedExternalLink>
                    ) : (
                      <ClearedLink
                        to={groupItem.url}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isMobileWidth()) {
                            dispatch(toggleCMSSidebar(true));
                          }
                        }}
                      >
                        <Tooltip
                          tooltip={
                            isSidebarHidden ? t(groupItem.title) : undefined
                          }
                          right
                        >
                          <span
                            style={{
                              marginRight: isSidebarHidden ? '0' : '10px',
                            }}
                          >
                            {groupItem.icon}
                          </span>
                        </Tooltip>

                        <div key={groupItem.title} className="item-text">
                          {t(groupItem.title)}
                        </div>
                      </ClearedLink>
                    )}
                  </div>
                </div>
              );
            }

            if (
              user &&
              user.permissions &&
              groupsWithoutPermissions &&
              groupsWithoutPermissions[groupItem.title] === false
            ) {
              return (
                <CollapsibleMenu
                  key={index}
                  groupItem={groupItem}
                  index={index}
                  isSidebarHidden={isSidebarHidden}
                  isActualOpened={openedMenuItems.includes(index)}
                  dispatch={dispatch}
                  user={user}
                  isAllowedToSeePages={isAllowedToSeePages}
                />
              );
            }

            return <React.Fragment key={groupItem.title} />;
          })}
        </div>
        <div className="utility-items-wrapper">
          <FeedBack isExpanded={!isSidebarHidden} />
          <Avatar isExpanded={!isSidebarHidden} />
        </div>
      </div>
    </>
  );
};

export default SidebarCMS;
